/** Tab „Profile": Avatar, Banner, Bio, Pronomen, Bannerfarbe, Profil-Themefarben mit Live-Vorschau. */
import { FIXED_LIMITS } from '@lagerfeuer/shared';
import { useState, type FormEvent } from 'react';
import { errorMessage } from '../../lib/api';
import { usersApi } from '../../lib/endpoints';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { ProfileCard } from '../profile/ProfileCard';
import { ImageUpload } from './ImageUpload';
import { applyMe, validHex } from './useSettings';

export function ProfileTab() {
  const me = useAppStore((s) => s.me);
  if (!me) return null;
  return <ProfileEditor key={me.id} />;
}

interface Draft {
  bio: string;
  pronouns: string;
  bannerColor: string | null;
  themePrimary: string | null;
  themeAccent: string | null;
}

function ProfileEditor() {
  const me = useAppStore((s) => s.me)!;
  const maxBio = useAppStore((s) => s.limits.maxBioLength);
  const initial: Draft = { bio: me.bio ?? '', pronouns: me.pronouns ?? '', bannerColor: me.bannerColor, themePrimary: me.themePrimary, themeAccent: me.themeAccent };
  const [draft, setDraft] = useState<Draft>(initial);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const dirty = (Object.keys(initial) as (keyof Draft)[]).some((k) => (draft[k] ?? '') !== (initial[k] ?? ''));
  const bioTooLong = maxBio > 0 && draft.bio.length > maxBio;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (bioTooLong) return;
    setBusy(true);
    setError(null);
    try {
      const res = await usersApi.updateMe({
        bio: draft.bio.trim() || null,
        pronouns: draft.pronouns.trim() || null,
        bannerColor: validHex(draft.bannerColor),
        themePrimary: validHex(draft.themePrimary),
        themeAccent: validHex(draft.themeAccent),
      });
      applyMe(res);
      setDraft({ bio: res.bio ?? '', pronouns: res.pronouns ?? '', bannerColor: res.bannerColor, themePrimary: res.themePrimary, themeAccent: res.themeAccent });
      toast.success('Profil gespeichert');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lf-profile-editor">
      <form className="stack lf-profile-form" onSubmit={save} data-testid="profile-form">
        <ImageUpload
          label="Avatar"
          kind="avatar"
          currentUrl={me.avatarUrl}
          onPreview={setAvatarPreview}
          onUpload={async (f) => applyMe(await usersApi.setAvatar(f))}
          onRemove={async () => applyMe(await usersApi.removeAvatar())}
          testId="avatar-upload"
        />
        <ImageUpload
          label="Banner"
          kind="banner"
          currentUrl={me.bannerUrl}
          onPreview={setBannerPreview}
          onUpload={async (f) => applyMe(await usersApi.setBanner(f))}
          onRemove={async () => applyMe(await usersApi.removeBanner())}
          testId="banner-upload"
        />
        <ColorField label="Bannerfarbe" hint="Wird gezeigt, wenn kein Banner-Bild gesetzt ist." value={draft.bannerColor} onChange={(v) => set('bannerColor', v)} testId="banner-color" />
        <div className="row lf-color-pair">
          <ColorField label="Profilfarbe primär" value={draft.themePrimary} onChange={(v) => set('themePrimary', v)} testId="theme-primary" />
          <ColorField label="Profilfarbe Akzent" value={draft.themeAccent} onChange={(v) => set('themeAccent', v)} testId="theme-accent" />
        </div>
        <div className="field">
          <label htmlFor="profile-pronouns">Pronomen</label>
          <input
            id="profile-pronouns"
            className="input"
            value={draft.pronouns}
            onChange={(e) => set('pronouns', e.target.value)}
            maxLength={FIXED_LIMITS.pronounsMax}
            placeholder="z. B. sie/ihr"
            data-testid="profile-pronouns"
          />
        </div>
        <div className="field">
          <label htmlFor="profile-bio">Über mich</label>
          <textarea
            id="profile-bio"
            className="textarea"
            value={draft.bio}
            onChange={(e) => set('bio', e.target.value)}
            rows={5}
            aria-invalid={bioTooLong}
            aria-describedby="profile-bio-count"
            data-testid="profile-bio"
          />
          <span id="profile-bio-count" className={bioTooLong ? 'error-text' : 'field-hint'} data-testid="profile-bio-count">
            {draft.bio.length}
            {maxBio > 0 ? ` / ${maxBio}` : ''} Zeichen · Markdown möglich
          </span>
        </div>
        {error && (
          <p className="error-text" role="alert" data-testid="profile-error">
            {error}
          </p>
        )}
        <div className="row lf-sticky-save" data-dirty={dirty || undefined}>
          {dirty && <span className="small muted">Ungespeicherte Änderungen</span>}
          <span className="spacer" />
          <button type="button" className="btn btn-ghost" onClick={() => setDraft(initial)} disabled={!dirty || busy} data-testid="profile-reset">
            Zurücksetzen
          </button>
          <button type="submit" className="btn btn-primary" disabled={!dirty || busy || bioTooLong} data-testid="profile-save">
            Speichern
          </button>
        </div>
      </form>
      <aside className="lf-profile-preview" aria-label="Vorschau">
        <div className="label">Vorschau</div>
        <ProfileCard
          user={{ ...me, bio: draft.bio || null, pronouns: draft.pronouns || null, bannerColor: draft.bannerColor, themePrimary: draft.themePrimary, themeAccent: draft.themeAccent }}
          avatarUrl={avatarPreview}
          bannerUrl={bannerPreview}
          status={me.status}
          customStatus={me.customStatus}
          testId="profile-preview"
        />
      </aside>
    </div>
  );
}

export function ColorField({ label, value, onChange, hint, testId }: { label: string; value: string | null; onChange: (v: string | null) => void; hint?: string; testId: string }) {
  const id = `color-${testId}`;
  return (
    <div className="field lf-color-field">
      <label htmlFor={id}>{label}</label>
      <div className="row">
        <input id={id} type="color" className="lf-color-input" value={validHex(value) ?? '#ff7a18'} onChange={(e) => onChange(e.target.value)} data-testid={testId} data-empty={value ? undefined : 'true'} />
        <span className="small muted" data-testid={`${testId}-value`}>
          {value ?? 'Standard'}
        </span>
        {value && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(null)} data-testid={`${testId}-clear`}>
            Zurücksetzen
          </button>
        )}
      </div>
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}
